import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { track } from "@/lib/analytics";
import { useSupport } from "@/hooks/useSupport";

interface HelpfulVoteProps {
  articleSlug: string;
  topicSlug: string;
}

/**
 * "Was this helpful?" widget at the bottom of every help article.
 *
 * - Optimistic — the vote is recorded locally immediately and posted to
 *   the API in the background. We don't gate the UI on the network.
 * - Voting is anonymous; the API endpoint dedupes per
 *   (article_slug, vote_signature) so refreshing won't double-count.
 * - When the user votes "no" we surface a CTA to chat with support so the
 *   article being unhelpful directly leads to a conversation.
 */
export function HelpfulVote({ articleSlug, topicSlug }: HelpfulVoteProps) {
  const { t, i18n } = useTranslation();
  const support = useSupport();
  const [voted, setVoted] = useState<"up" | "down" | null>(null);

  const submit = useCallback(
    (vote: "up" | "down") => {
      // Functional updater so two synchronous clicks on the same render
      // (rapid double-click) only register the FIRST one. Reading the
      // closed-over `voted` state would let both clicks see `null` and
      // both fire, producing duplicate analytics + POSTs.
      let firstClick = false;
      setVoted((prev) => {
        if (prev) return prev;
        firstClick = true;
        return vote;
      });
      if (!firstClick) return;
      track("help_helpful_vote", {
        article_slug: articleSlug,
        vote: vote === "up" ? "yes" : "no",
        locale: i18n.language,
      });
      // Fire-and-forget — the help feedback API is intentionally cheap and
      // never throws. We don't await it so the UI updates instantly.
      void fetch("/api/help/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          article_slug: articleSlug,
          topic_slug: topicSlug,
          vote,
          locale: i18n.language,
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }),
      }).catch(() => {
        // Already tracked client-side; failure here is non-fatal.
      });
    },
    [articleSlug, topicSlug, i18n.language],
  );

  if (voted === "up") {
    return (
      <div
        className="mt-10 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900"
        data-testid="help-vote-thanks"
      >
        {t("helpCentre.feedback.thanks_up", { defaultValue: "Glad we could help. Thanks for the feedback." })}
      </div>
    );
  }

  if (voted === "down") {
    return (
      <div
        className="mt-10 rounded-xl border border-amber-200 bg-amber-50 p-5"
        data-testid="help-vote-thanks-down"
      >
        <p className="text-sm font-semibold text-amber-900">
          {t("helpCentre.feedback.thanks_down_title", { defaultValue: "Sorry this didn't help." })}
        </p>
        <p className="mt-1 text-sm text-amber-900/90">
          {t("helpCentre.feedback.thanks_down_body", { defaultValue: "Get in touch and our team will look into your specific case." })}
        </p>
        <button
          type="button"
          onClick={() => {
            track("help_intercom_cta_click", {
              article_slug: articleSlug,
              surface: "vote_negative",
              channel: "chat",
            });
            support.openChat({
              eventName: "help_article_contact_after_unhelpful",
              context: { article_slug: articleSlug, topic_slug: topicSlug },
              prefill: t("helpCentre.feedback.contact_prefill", {
                defaultValue: "I read the “{{slug}}” help article but it didn't answer my question.",
                slug: articleSlug,
              }),
            });
          }}
          className="mt-3 inline-flex items-center rounded-full bg-amber-900 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-950"
          data-testid="help-vote-contact-button"
        >
          {t("helpCentre.feedback.contact_button", { defaultValue: "Chat with support" })}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-10 flex flex-col gap-3 rounded-xl border border-border bg-muted/30 p-5 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-semibold text-foreground">
        {t("helpCentre.feedback.question", { defaultValue: "Was this article helpful?" })}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => submit("up")}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground hover:border-primary hover:text-primary"
          data-testid="help-vote-up"
        >
          <ThumbsUp className="h-4 w-4" aria-hidden />
          {t("helpCentre.feedback.yes", { defaultValue: "Yes" })}
        </button>
        <button
          type="button"
          onClick={() => submit("down")}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-foreground hover:border-primary hover:text-primary"
          data-testid="help-vote-down"
        >
          <ThumbsDown className="h-4 w-4" aria-hidden />
          {t("helpCentre.feedback.no", { defaultValue: "No" })}
        </button>
      </div>
    </div>
  );
}
